import './ui/style.css';

import { World } from './engine/ecs';
import { GameLoop } from './engine/loop';
import { randomRng } from './engine/rng';
import { GameStore } from './core/store';
import { deleteSave, loadGame, localStorageBackend, saveGame } from './core/save';
import { OfflineAdapter, type NetworkAdapter } from './core/net/NetworkAdapter';
import { createValleDelleFaglie } from './world/maps/valleDelleFaglie';
import { GameRenderer } from './render/renderer';
import { InputManager } from './input/input';
import { createTouchControls } from './input/joystick';
import { Collider, Facing, PlayerControlled, Position, Velocity } from './game/components';
import { MovementSystem } from './game/systems/movement';
import { EncounterSystem } from './game/systems/encounter';
import { interactionInFront } from './game/systems/interaction';
import { createLumine, healFull, isFainted } from './game/lumine';
import { getSpecies } from './game/data/species';
import { DialogUi } from './ui/dialog';
import { Hud } from './ui/hud';
import { BattleUi } from './ui/battleUi';
import { chooseStarter } from './ui/starterSelect';
import { openTeamMenu } from './ui/teamMenu';

/** Durata di un ciclo giorno/notte completo, in secondi reali. */
const DAY_LENGTH = 300;

async function boot(): Promise<void> {
  const gameRoot = document.getElementById('game-root')!;
  const uiRoot = document.getElementById('ui-root')!;

  // — Stato e servizi —
  const backend = localStorageBackend();
  const saved = loadGame(backend);
  const store = new GameStore(saved ?? undefined);
  const rng = randomRng();
  // Fase 2: sostituire OfflineAdapter con un WebSocketAdapter.
  const network: NetworkAdapter = new OfflineAdapter();
  void network;

  // — Mondo e render —
  const mapDef = createValleDelleFaglie();
  const renderer = await GameRenderer.create(gameRoot, mapDef);

  // — Input —
  const input = new InputManager();
  input.attach();
  createTouchControls(uiRoot, input);

  // — UI —
  const dialog = new DialogUi(uiRoot);
  const hud = new Hud(uiRoot, store);
  const battleUi = new BattleUi(uiRoot, store, rng);

  // — ECS —
  const world = new World();
  const player = world.createEntity();
  const startPos =
    store.state.player.pos.x !== 0 || store.state.player.pos.y !== 0
      ? store.state.player.pos
      : mapDef.spawn;
  world.add(player, Position, { x: startPos.x, y: startPos.y });
  world.add(player, Velocity, { vx: 0, vy: 0, speed: 150 });
  world.add(player, Facing, { dir: store.state.player.dir, walking: false });
  world.add(player, Collider, { width: 16, height: 14 });
  world.add(player, PlayerControlled, { encounterMeter: 0 });
  renderer.camera.snapTo(startPos.x, startPos.y);

  let menuOpen = false;
  const canExplore = () => store.state.phase === 'explore' && !dialog.isOpen && !menuOpen;

  world.addSystem(new MovementSystem(input, mapDef.map, canExplore));
  world.addSystem(
    new EncounterSystem(mapDef, mapDef.map, rng, canExplore, (wild, source) => {
      void startBattle(wild, source);
    }),
  );

  // — Salvataggio —
  const persist = () => {
    const pos = world.require(player, Position);
    const facing = world.require(player, Facing);
    store.state.player.pos = { x: pos.x, y: pos.y };
    store.state.player.dir = facing.dir;
    saveGame(backend, store.state);
    store.events.emit('saved', { at: Date.now() });
  };

  // — Battaglia —
  async function startBattle(
    wild: ReturnType<typeof createLumine>,
    source: 'tallGrass' | 'anomaly',
  ): Promise<void> {
    if (store.state.player.team.every((l) => isFainted(l))) return;
    store.setPhase('battle');
    hud.setVisible(false);
    const outcome = await battleUi.start(store.state.player.team, wild);
    hud.setVisible(true);

    if (outcome === 'captured') {
      if (store.addToTeam(wild)) {
        store.notice(`${getSpecies(wild.speciesId).name} si è unito alla squadra!`);
      } else {
        store.notice('Squadra piena: il Lumine torna libero nella Valle.');
      }
    } else if (outcome === 'defeat') {
      for (const lumine of store.state.player.team) healFull(lumine);
      const pos = world.require(player, Position);
      pos.x = mapDef.spawn.x;
      pos.y = mapDef.spawn.y;
      renderer.camera.snapTo(pos.x, pos.y);
      await dialog.say(
        [
          'I tuoi Lumini sono esausti…',
          'Ti risvegli alla Stazione di Risonanza. La squadra è di nuovo in forze.',
        ],
        'Dott.ssa Mira',
      );
    } else if (outcome === 'victory' && source === 'anomaly') {
      store.notice("L'anomalia si placa per un momento…");
    }
    store.notifyTeamChanged();
    store.setPhase('explore');
    persist();
  }

  // — Interazioni —
  input.on('interact', () => {
    if (dialog.handleInteract()) return;
    if (!canExplore()) return;
    const interaction = interactionInFront(world, player, mapDef.map);
    if (interaction.kind === 'healStation') {
      for (const lumine of store.state.player.team) healFull(lumine);
      store.notifyTeamChanged();
      void dialog
        .say(
          ['Benvenuto alla Stazione di Risonanza!', 'I tuoi Lumini sono stati risintonizzati: PS al massimo.'],
          'Dott.ssa Mira',
        )
        .then(persist);
    } else if (interaction.kind === 'anomalyInfo') {
      void dialog.say(
        [
          'La faglia pulsa con una luce viola…',
          "Cammina sull'anomalia: i Lumini più rari si manifestano qui.",
        ],
        'Taccuino del Custode',
      );
    }
  });

  input.on('menu', () => {
    if (store.state.phase !== 'explore' || dialog.isOpen || menuOpen) return;
    menuOpen = true;
    void openTeamMenu(uiRoot, store, {
      onSave: () => {
        persist();
        store.notice('Partita salvata!');
      },
      onNewGame: () => {
        deleteSave(backend);
        window.location.reload();
      },
    }).then(() => {
      menuOpen = false;
    });
  });

  // — Game loop —
  const loop = new GameLoop({
    update(dt) {
      input.update();
      world.update(dt);
      if (store.state.phase === 'explore') {
        store.state.timeOfDay = (store.state.timeOfDay + dt / DAY_LENGTH) % 1;
      }
    },
    render() {
      const pos = world.require(player, Position);
      const facing = world.require(player, Facing);
      renderer.updatePlayer(pos.x, pos.y, facing.dir, facing.walking && canExplore());
      renderer.render(1 / 60, store.state.timeOfDay, pos.x, pos.y);
    },
  });
  loop.start();

  // — Intro / nuova partita —
  if (store.state.player.team.length === 0) {
    store.setPhase('intro');
    await dialog.say(
      [
        'Benvenuto sul pianeta Lumen, giovane Custode di Faglia!',
        'Le anomalie magnetiche della Valle generano i Lumini: creature di luce che dobbiamo studiare e proteggere.',
        'Il tuo compito: esplorare la Valle, stabilizzare le faglie e stringere legami con i Lumini.',
        'Ma prima… scegli il tuo compagno di ricerca!',
      ],
      'Dott.ssa Mira',
    );
    const starterId = await chooseStarter(uiRoot);
    store.addToTeam(createLumine(starterId, 5, rng));
    await dialog.say(
      [
        `Ottima scelta! ${getSpecies(starterId).name} sarà un compagno fedele.`,
        'I cespugli nascondono Lumini comuni; il cratere a sud-est, vicino alle anomalie viola, quelli più rari.',
        'Usa i Risonatori per catturarli, e torna alla Stazione (la casetta) per curare la squadra. Buona ricerca!',
      ],
      'Dott.ssa Mira',
    );
    persist();
  }
  store.setPhase('explore');
}

void boot();
